import React, { useCallback, useState } from "react";
import {
  View,
  Pressable,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
} from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import { notifySuccess } from "@components/ds/notify";
import { showApiError } from "@components/ds/showApiError";
import { t } from "@constants/localization";
import {
  getDsRoutingRecommended,
  assignDsRoutingTps,
} from "@services/api/dsRouting";
import RoutingHeader from "../components/RoutingHeader";
import { formatKm } from "../helpers";
import { C, RText, Tag, Check, AddressLine, tpId } from "../theme";

const PATH = "DsRouting/rec";
const errorKey = () => "DsRouting/errorGeneric";

// Registered as `DsRoutingRecommended`; opened from "Recommended route" on a
// routed D0 card with `{ tp }`. Pixel-matched to the mockup (§10 frame 11):
// the parent customer, the nearest unrouted customers near → far (ticked
// ones outlined green), and "Add N to <DB>'s route".
const DsRoutingRecommended = ({ navigation, route }) => {
  const tp = route?.params?.tp || {};
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useFocusEffect(
    useCallback(() => {
      let active = true;
      setLoading(true);
      getDsRoutingRecommended(tp.cx_id)
        .then(({ data: body }) => {
          if (!active) return;
          const payload = body?.data || null;
          setData(payload);
          setSelected((payload?.tps || []).map((item) => String(item.cx_id)));
        })
        .catch((error) => showApiError(error, errorKey))
        .finally(() => active && setLoading(false));
      return () => {
        active = false;
      };
    }, [tp.cx_id]),
  );

  const parent = data?.parent || null;
  const db = parent?.db || tp.db || null;
  const tps = data?.tps || [];

  const toggle = (cxId) =>
    setSelected((current) =>
      current.includes(cxId)
        ? current.filter((id) => id !== cxId)
        : current.concat(cxId),
    );

  const onAdd = async () => {
    if (!db || selected.length === 0) return;
    try {
      setSubmitting(true);
      await assignDsRoutingTps({ cx_ids: selected, cp_db_id: db.cp_db_id });
      notifySuccess(t("DsRouting/toast/assigned", { name: db.name || "" }));
      navigation.goBack();
    } catch (error) {
      showApiError(error, errorKey);
    } finally {
      setSubmitting(false);
    }
  };

  const disabled = !db || selected.length === 0 || submitting;

  return (
    <View style={styles.screen}>
      <RoutingHeader title={t(`${PATH}/header`)} navigation={navigation} />
      {loading && !data ? (
        <ActivityIndicator size="large" color={C.red} style={styles.loader} />
      ) : (
        <ScrollView contentContainerStyle={styles.body}>
          <View style={styles.forRow}>
            <Tag
              label={t(`${PATH}/title`)}
              bg={C.blueBg}
              color={C.blue}
              size={10}
              style={styles.recTag}
            />
            <RText size={11} weight={600} color={C.muted} style={styles.grow}>
              {t(`${PATH}/for`)}
              <RText size={11} weight={700}>
                {t(`${PATH}/dbName`, { name: db?.name || "" })}
              </RText>
              {t(`${PATH}/tapped`)}
            </RText>
          </View>

          <View style={styles.parent}>
            <RText size={10} weight={700} color={C.muted} style={styles.label}>
              {t(`${PATH}/parent`)}
            </RText>
            <RText size={16} weight={700} style={styles.parentName}>
              {parent?.cx_name || tpId(tp.cx_id)}
            </RText>
            <AddressLine address={parent?.address} />
            <RText size={12} weight={500} color={C.muted} style={styles.meta}>
              {t(`${PATH}/parentMeta`, {
                area: parent?.area || tp.area || "",
                cx_id: tp.cx_id,
                db: db?.name || "",
              })}
            </RText>
          </View>

          <RText size={9} weight={700} color={C.muted} style={styles.label}>
            {t(`${PATH}/nearest`, { count: String(tps.length) })}
          </RText>
          {tps.length === 0 && !loading ? (
            <RText size={13} weight={500} color={C.muted} style={styles.empty}>
              {t(`${PATH}/empty`)}
            </RText>
          ) : null}
          {tps.map((item) => {
            const cxId = String(item.cx_id);
            const checked = selected.includes(cxId);
            return (
              <Pressable
                key={cxId}
                testID={`ds-routing-rec-${cxId}`}
                onPress={() => toggle(cxId)}
                style={[styles.row, checked && styles.rowOn]}
              >
                <View style={styles.grow}>
                  <RText size={15} weight={700}>
                    {item.cx_name || tpId(cxId)}
                  </RText>
                  <AddressLine address={item.address} />
                  <RText
                    size={12}
                    weight={500}
                    color={C.muted}
                    style={styles.meta}
                  >
                    {t(`${PATH}/rowMeta`, {
                      area: item.area || "",
                      cx_id: cxId,
                      count: String(item.order_count ?? 0),
                    })}
                  </RText>
                </View>
                <View style={styles.distance}>
                  <RText size={15} weight={700} color={C.green}>
                    {formatKm(item.distance_km) || "--"}
                  </RText>
                  <RText size={10} weight={600} color={C.muted}>
                    {t(`${PATH}/away`)}
                  </RText>
                </View>
                <View style={[styles.box, checked && styles.boxOn]}>
                  {checked ? <Check /> : null}
                </View>
              </Pressable>
            );
          })}
        </ScrollView>
      )}
      <View style={styles.footer}>
        <Pressable
          testID="ds-routing-rec-add"
          disabled={disabled}
          onPress={onAdd}
          style={[styles.cta, disabled && styles.ctaDisabled]}
        >
          {submitting ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <RText
              size={16}
              weight={600}
              color={disabled ? C.disabledInk : "#FFFFFF"}
            >
              {t(`${PATH}/addTo`, {
                count: String(selected.length),
                name: db?.name || "",
              })}
            </RText>
          )}
        </Pressable>
      </View>
    </View>
  );
};

export default DsRoutingRecommended;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  loader: { flex: 1 },
  body: { paddingTop: 12, paddingHorizontal: 16, paddingBottom: 24, gap: 11 },
  forRow: { flexDirection: "row", alignItems: "center", gap: 7 },
  recTag: { paddingVertical: 4, paddingHorizontal: 10 },
  grow: { flex: 1 },
  parent: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    padding: 14,
  },
  label: { letterSpacing: 0.6 },
  parentName: { paddingTop: 3 },
  meta: { paddingTop: 2 },
  empty: { textAlign: "center", marginTop: 24 },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 12,
    paddingVertical: 13,
    paddingHorizontal: 14,
  },
  rowOn: { borderColor: C.green },
  distance: { alignItems: "flex-end", flexShrink: 0 },
  box: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: C.border,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  boxOn: { backgroundColor: C.green, borderColor: C.green },
  footer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: C.surface,
    borderTopWidth: 1,
    borderTopColor: C.border,
  },
  cta: {
    height: 52,
    borderRadius: 9,
    backgroundColor: C.red,
    alignItems: "center",
    justifyContent: "center",
  },
  ctaDisabled: { backgroundColor: C.disabledBg },
});
